import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { LuCheck, LuCrown } from "react-icons/lu";
import {
  getPlans,
  getMySubscription,
  createPaymentOrder,
  verifyPayment,
  loadRazorpayScript,
} from "../../Services/proService";
import { successToast, errorToast } from "../../utils/toasts";

const parseBenefits = (plan) => {
  const b = plan?.plan_benefits;
  if (Array.isArray(b)) return b;
  if (typeof b === "string") {
    try {
      const arr = JSON.parse(b);
      if (Array.isArray(arr)) return arr;
    } catch {
      return b.split(/\n|,/).map((s) => s.trim()).filter(Boolean);
    }
  }
  return [];
};

const ProPlans = () => {
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const user = useSelector((s) => s.auth?.user);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([getPlans(), getMySubscription()]);
      setPlans(p || []);
      setSub(s);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubscribe = async (plan) => {
    setPaying(plan.id);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Failed to load payment gateway.");
      const order = await createPaymentOrder(plan.id);

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: Math.round(order.amount * 100),
        currency: order.currency || "INR",
        name: "Atmanirbhar Fins",
        description: `${plan.title} subscription`,
        order_id: order.razorpay_order_id,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.mobile_number || "",
        },
        theme: { color: "#425F80" },
        handler: async (response) => {
          try {
            await verifyPayment({
              order_id: order.order_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            successToast("You are now a Pro member!");
            load();
          } catch (e) {
            errorToast("Payment verification failed. Contact support.");
          }
        },
        modal: { ondismiss: () => setPaying(null) },
      });
      rzp.on("payment.failed", () => errorToast("Payment failed. Try again."));
      rzp.open();
    } catch (e) {
      errorToast(e?.message || "Unable to start payment.");
    } finally {
      setPaying(null);
    }
  };

  if (loading) {
    return <div className="page-container p-6">Loading plans…</div>;
  }

  return (
    <div className="page-container p-6">
      <div className="flex items-center gap-2 mb-1">
        <LuCrown className="text-amber-500" size={26} />
        <h1 className="text-2xl font-bold text-gray-800">Advisor Pro</h1>
      </div>
      <p className="text-gray-500 mb-6">
        Unlock certification, high-value leads, priority support and advanced analytics.
      </p>

      {sub?.isPro && sub?.active && (
        <div className="mb-6 rounded-xl bg-green-50 border border-green-200 px-5 py-4">
          <p className="text-green-800 font-semibold">You are a Pro member 🎉</p>
          <p className="text-sm text-green-700">
            Valid till {moment(sub.active.end_date).format("DD MMM YYYY")}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {plans.length === 0 && (
          <p className="text-gray-400">No plans available yet.</p>
        )}
        {plans.map((plan) => {
          const benefits = parseBenefits(plan);
          return (
            <div
              key={plan.id}
              className="rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col bg-white"
            >
              <h3 className="text-lg font-semibold text-gray-800">{plan.title}</h3>
              <p className="text-sm text-gray-500 mb-4">{plan.description}</p>
              <div className="mb-4">
                <span className="text-3xl font-bold text-gray-900">
                  ₹{Number(plan.total_price ?? plan.price).toLocaleString("en-IN")}
                </span>
                <span className="text-gray-400 text-sm"> / {plan.duration}</span>
              </div>
              <ul className="flex-1 space-y-2 mb-6">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <LuCheck className="text-green-500 mt-0.5 shrink-0" size={16} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => handleSubscribe(plan)}
                disabled={paying === plan.id || (sub?.isPro && sub?.active)}
                className="w-full rounded-full bg-[#425F80] text-white py-2.5 font-medium disabled:opacity-50"
              >
                {sub?.isPro && sub?.active
                  ? "Already Pro"
                  : paying === plan.id
                  ? "Processing…"
                  : "Upgrade Now"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProPlans;
